import React, { useEffect, useState } from 'react'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { Action } from 'types/GlobalUserEvents'

import { EPublicationStatus } from 'types/Enums'

import { useReportActions } from 'redux/actions/Reports'
import { useReportEditionActions } from 'redux/actions/ReportEditions'
import { useResourceStatusActions } from 'redux/actions/ResourceStatuses'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useHistory, useLocation } from 'react-router-dom'
import { useParams, useQuery } from 'utils/hooks'
import { useReport } from 'redux/hooks/Reports'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import DisabledWrapper from 'components_new/molecules/DisabledWrapper'
import DndBlocks from 'components_new/molecules/DndBlocks'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import DataStatus from 'components_new/organisms/DataStatus'
import ReportFilters from 'components_new/organisms/ReportFilters'
import ReportBlock from 'components_new/organisms/ReportBlock'
import ReportEditionsTable from 'components_new/organisms/ReportEditionsTable'
import ReportHeader from 'components_new/organisms/ReportHeader'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import ReportTemplate from 'components_new/templates/ReportTemplate'

import { getContext } from 'utils/userEvents'

import MetricsDialog from 'components_new/organisms/dialogs/MetricsDialog'
import { REPORT_WIDGET_DEFAULT_HEIGHT } from 'components_new/organisms/ReportWidget'

import { getMetricTitle } from 'utils/metric'
import { useWidgetActions } from 'redux/actions/Widgets'

function getOrderedItems<T extends { id: string }>(
  items: T[],
  order: string[]
) {
  return order
    .map((id) => items.find((item) => item.id === id))
    .filter((bid): bid is T => !!bid)
}

const Report = () => {
  const { id } = useParams<{ id: string }>()

  const history = useHistory()
  const { isAdmin } = useAccountSettings()
  const {
    tryCopyReport,
    tryCreateReportWidget,
    tryDeleteReport,
    tryReorderBlocks,
    tryReorderBlockWidgets,
    tryToggleReportStatus,
    tryVisitReport
  } = useReportActions()
  const { tryCreateReportEdition } = useReportEditionActions()
  const { invalidateWidgets } = useWidgetActions()
  const { tryPutResourceStatus } = useResourceStatusActions()

  const report = useReport(id)
  const { lang, t, translateEnum } = useTranslation()

  const { query } = useQuery()
  const [editMode, setEditMode] = useState<boolean>(Boolean(query['edit']))

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>(
    {}
  )

  const [blockOrder, setBlockOrder] = useState<string[]>([])
  const orderedBlocks = report ? getOrderedItems(report.blocks, blockOrder) : []

  const [widgetOrders, setWidgetOrders] = useState<
    Record<string, string[] | undefined>
  >({})

  const handleReorderWidgets = (blockId: string) => (newOrder: string[]) => {
    if (!report) return

    tryReorderBlockWidgets(report.id, blockId, newOrder)
    setWidgetOrders((prev) => ({ ...prev, [blockId]: newOrder }))
  }

  const handleSetActiveFilters = (
    newFilterValue: React.SetStateAction<Record<string, string[]>>
  ) => {
    invalidateWidgets()
    setActiveFilters(newFilterValue)
  }

  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)

  const [dialog, setDialog] = useState<{
    blockId: string | undefined
    open: boolean
    value: string
    variant: 'add' | 'metric'
  }>({
    blockId: undefined,
    open: false,
    value: '',
    variant: 'add'
  })

  /*-- handlers --*/
  const handleAddEdition = () => {
    tryCreateReportEdition({ report_id: id }, (newEditionId) =>
      history.push(`/reports/${id}/editions/${newEditionId}?edit=true`)
    )
  }

  const handleClickAdd = (blockId?: string) => {
    setDialog((prevState) => ({
      ...prevState,
      blockId,
      open: true,
      variant: 'add'
    }))
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({
      ...prevState,
      blockId: undefined,
      open: false,
      value: ''
    }))
  }

  const handleAddTextWidget = () => {
    if (dialog.blockId) {
      tryCreateReportWidget(id, {
        kpi_id: null,
        report_block_id: dialog.blockId
      })
    } else {
      tryCreateReportWidget(id, { kpi_id: null, report_block_id: null })
    }

    handleCloseDialog()
  }

  useEffect(() => {
    if (id) {
      const context = getContext(searchParams)

      UserEvents.log({
        action: Action.REPORT_OPENED,
        context: context,
        report_id: id
      })

      tryVisitReport(id)
    }
  }, [id])

  useEffect(() => {
    if (report) {
      setBlockOrder(report.blocks.map((b) => b.id))
      setWidgetOrders(
        Object.fromEntries(
          report.blocks.map((b) => [b.id, b.widgets.map((w) => w.id)])
        )
      )
    }
  }, [report?.blocks])

  // loading
  if (!report) return <></>

  // success
  return (
    <>
      <ReportTemplate
        content={{ editMode: { active: editMode } }}
        rightSidebar={{
          size: 'small'
        }}
        topbar={{
          actions: isAdmin ? (
            <Button
              onClick={() => setEditMode(!editMode)}
              variant={editMode ? 'contained' : 'text'}
            >
              {t('global', editMode ? 'done' : 'edit')}
            </Button>
          ) : null,
          content: (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip
                title={
                  report.favorite
                    ? t('dashboards', 'remove_favorite_tooltip')
                    : t('dashboards', 'add_favorite_tooltip')
                }
              >
                <IconButton
                  onClick={() =>
                    tryPutResourceStatus(id, 'report', report.favorite)
                  }
                  size="small"
                >
                  <Icon
                    color={report.favorite ? 'primary' : 'inherit'}
                    fontSize="small"
                    name={
                      report.favorite ? 'StarOutlined' : 'StarBorderOutlined'
                    }
                  />
                </IconButton>
              </Tooltip>
            </Box>
          ),
          options: [
            {
              iconName:
                report.status === EPublicationStatus.PUBLISHED
                  ? 'Unpublished'
                  : 'Published',
              onClick: () => tryToggleReportStatus(id, report.status),
              title:
                report.status === EPublicationStatus.PUBLISHED
                  ? t('global', 'unpublish')
                  : t('global', 'publish')
            },
            {
              iconName: 'Duplicate',
              onClick: () =>
                tryCopyReport(id, (newId) => history.push(`/reports/${newId}`)),
              title: t('global', 'duplicate')
            },
            {
              iconName: 'Delete',
              onClick: () => setDeleteOpen(true),
              title: t('global', 'delete')
            }
          ]
        }}
      >
        <ReportHeader editMode={editMode} id={id} sx={{ mb: 4 }} />

        <DisabledWrapper disabled={editMode}>
          <Box
            sx={{
              mb: 4
            }}
          >
            <SectionHeader
              actions={
                report.editions.length > 0 ? (
                  <Button
                    disabled={editMode}
                    onClick={handleAddEdition}
                    size="small"
                    startIcon={<Icon name="Add" />}
                  >
                    {t('global', 'create')} {t('global', 'edition', true)}
                  </Button>
                ) : undefined
              }
              color="secondary"
              divider={true}
              gutterBottom={true}
              icon={{ name: 'Published' }}
              title={'Utgåvor'}
              to={`/reports/${id}/editions`}
            />

            <ReportEditionsTable preview={true} reportId={id} />
          </Box>
        </DisabledWrapper>

        <SectionHeader
          actions={
            orderedBlocks.length > 0 ? <DataStatus align="right" /> : undefined
          }
          divider={true}
          icon={{ name: 'Template' }}
          title={'Mall'}
          sx={{ mb: 4 }}
        />

        <ReportFilters
          activeFilters={activeFilters}
          editMode={editMode}
          id={id}
          setActiveFilters={handleSetActiveFilters}
          sx={{ mb: 4 }}
        />

        {orderedBlocks.length === 0 ? (
          <EmptyPlaceholder
            actions={
              editMode ? (
                <Button onClick={() => handleClickAdd()}>
                  {t('reports', 'empty_widgets_cta')}
                </Button>
              ) : undefined
            }
            background={true}
            body={t('reports', 'empty_widgets_body')}
            height={172}
            size="small"
            title={t('reports', 'empty_widgets_title')}
          />
        ) : (
          <DndBlocks
            addBlock={{
              height: REPORT_WIDGET_DEFAULT_HEIGHT,
              onClick: () => handleClickAdd()
            }}
            editable={editMode}
            handleDragEnd={(newOrder) => {
              setBlockOrder(newOrder)
              tryReorderBlocks(id, newOrder)
            }}
            items={orderedBlocks.map((block) => {
              const order = widgetOrders[block.id]
              const orderedWidgets = order
                ? getOrderedItems(block.widgets, order)
                : block.widgets

              return {
                content: (
                  <ReportBlock
                    activeFilters={activeFilters}
                    block={{ ...block, widgets: orderedWidgets }}
                    editMode={editMode}
                    onAddWidget={() => {
                      handleClickAdd(block.id)
                    }}
                    onReorderWidgets={handleReorderWidgets(block.id)}
                    variant="template"
                  />
                ),
                id: block.id
              }
            })}
          />
        )}
      </ReportTemplate>

      {/*-- delete dialog --*/}
      <DeleteDialog
        onClose={() => setDeleteOpen(false)}
        onDelete={(callback) =>
          tryDeleteReport(id, () => {
            callback()
          })
        }
        open={deleteOpen}
        title={report.title}
        type={t('global', 'report', true)}
      />

      {/*-- dialogs --*/}
      <SimpleDialog
        onClose={handleCloseDialog}
        open={dialog.open && dialog.variant === 'add'}
      >
        <GenericListItem
          action="add"
          icon={{ name: 'Metric' }}
          onClick={() =>
            setDialog((prevState) => ({
              ...prevState,
              variant: 'metric'
            }))
          }
          title={`${t('global', 'add')} ${t('metrics', 'metric', true)}`}
        />

        <GenericListItem
          action="add"
          icon={{ name: 'RichText' }}
          onClick={handleAddTextWidget}
          title={`${t('global', 'add')} ${t('global', 'text', true)}`}
        />
      </SimpleDialog>

      <MetricsDialog
        availableSteps={['slices', 'distribution', 'rollingSum']}
        creating={false}
        handleOnAdd={(metric, settings) => {
          const title = getMetricTitle(metric, settings, lang, translateEnum)

          tryCreateReportWidget(
            id,
            {
              kpi_id: metric.id,
              title,
              report_block_id: dialog.blockId ?? null,
              ...settings
            },
            (err) => {
              if (!err) handleCloseDialog()
            }
          )
        }}
        onClose={handleCloseDialog}
        open={dialog.open && dialog.variant === 'metric'}
        title={t('metrics', 'add_metric')}
        variant="reports"
      />
    </>
  )
}

export default Report
